import React, { useEffect, useRef, useState } from 'react';
import PlayerProfile, { prefetchProfile } from './PlayerProfile';
import { flip, reveal, snapRow } from './flip';

// STATS MODE for lists of player cards / rows (Trades, Free agents) — same gesture as the lineup board:
// hold a card for 1 second → the screen stops doing its normal thing (picking players) and that player's
// profile opens right under his card; tap another card to see his; hold a card 1 second again to go back.
// (The bar only shows during the second half of the hold, so a normal tap never flashes it.)
//
//   const stats = useStatsMode();
//   <button className={`… sm-card ${stats.cls(id)}`} {...stats.bind(id)} onClick={stats.click(id, normalClick)}>…</button>
//   <ProfileSlot stats={stats} id={id} pick={{ player_id, player_name, team, position, cap_hit }} … />
export default function useStatsMode() {
  const [statsMode, setStatsMode] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [holdId, setHoldId] = useState(null);       // the card being held
  const [quick, setQuick] = useState(false);        // switching from one profile to another (short fade, no full entrance)
  const holdTimer = useRef(null);
  const lastEl = useRef(null);                      // the card last pressed (to find the block it lives in)
  const start = useRef(null);
  const skipClick = useRef(false);                  // the click that ends a long press isn't a tap
  useEffect(() => () => { clearTimeout(holdTimer.current); }, []);

  // Every change goes through flip() (flip.js): the layout changes once, then the rows / cards slide to their new
  // place and the old profile is wiped away — nothing is animated by re-laying-out the page frame by frame.
  // The block that changes = the nearest [data-flip-root] around the card (the two rosters, the free-agents panels).
  const blockOf = (el) => el?.closest?.('[data-flip-root]') || document.querySelector('.pp-wrap:not(.pp-ghost)')?.closest('[data-flip-root]') || document.querySelector('[data-flip-root]');
  // in a scrolling list, the chosen row snaps to the same spot every time (near the top, the row before still showing)
  const snap = () => { const el = lastEl.current; if (el?.isConnected) snapRow(el.closest('.sm-rowwrap') || el); };
  const openWrap = (block) => block?.querySelector('.pp-wrap:not(.pp-ghost)') || null;
  const show = (id) => {
    if (id === profileId) return;
    const block = blockOf(lastEl.current);
    flip(block, () => { setQuick(true); setProfileId(id); }, { ghost: openWrap(block), settle: snap });
  };
  const exit = () => { const block = blockOf(lastEl.current); flip(block, () => { setStatsMode(false); setProfileId(null); }, { ghost: openWrap(block) }); };
  const enter = (id) => flip(blockOf(lastEl.current), () => { setQuick(false); setStatsMode(true); setProfileId(id); }, { settle: snap });
  const cancelHold = () => { clearTimeout(holdTimer.current); holdTimer.current = null; setHoldId(null); };

  const bind = (id) => ({
    onPointerDown: (e) => {
      if (e.button != null && e.button !== 0) return;
      cancelHold();
      lastEl.current = e.currentTarget;
      prefetchProfile(id);   // by the end of the hold (or the tap) his numbers are already here
      start.current = { x: e.clientX, y: e.clientY };
      setHoldId(id);
      holdTimer.current = setTimeout(() => {
        holdTimer.current = null; setHoldId(null);
        skipClick.current = true;
        setTimeout(() => { skipClick.current = false; }, 700);
        if (statsMode) exit(); else enter(id);
      }, 1000);
    },
    // moving (a scroll, a drag) is not a hold
    onPointerMove: (e) => { const s = start.current; if (holdTimer.current && s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 8) cancelHold(); },
    onPointerUp: cancelHold,
    onPointerLeave: cancelHold,
    onPointerEnter: () => { if (statsMode) prefetchProfile(id); },   // pointing at a card in stats mode = get his profile ready
    onPointerCancel: cancelHold,
    onContextMenu: (e) => { if (holdId || statsMode) e.preventDefault(); },
  });
  // the card's click: in stats mode it shows the profile, otherwise it does what it always did
  const click = (id, normal) => () => {
    if (skipClick.current) { skipClick.current = false; return; }
    if (statsMode) { show(id); return; }
    normal?.();
  };
  const cls = (id) => `${holdId === id ? 'holding' : ''} ${statsMode && profileId === id ? 'profiled' : ''}`;
  // for rows that do nothing else (the pool's List view): a plain TAP opens his profile under his row,
  // tapping another row moves it there, tapping the same row closes it — no hold needed
  const toggle = (id) => (e) => {
    lastEl.current = e.currentTarget;
    if (!statsMode) enter(id);
    else if (profileId === id) exit();
    else show(id);
  };

  return { statsMode, profileId, holdId, quick, bind, click, cls, exit, show, toggle };
}

// The profile under a card (only the open one: the one being left is a copy that flip() wipes away).
export function ProfileSlot({ stats, id, pick, lang, poolPoints = null, slot = null, game = null, compact = false, caret = '34px' }) {
  if (!(stats.statsMode && stats.profileId === id)) return null;
  return (
    <div className={`pp-wrap ${compact ? 'pp-compact' : ''}`} style={{ '--caret-x': caret }} ref={(el) => { if (el && !el.dataset.shown) { el.dataset.shown = '1'; reveal(el); } }}>
      <PlayerProfile pick={pick} lang={lang} poolPoints={poolPoints} slot={slot} game={game} compact={compact} quick={stats.quick} onClose={stats.exit} />
    </div>
  );
}

// the line that says which mode you're in
export function StatsHint({ stats, lang, className = '' }) {
  const fr = lang === 'fr';
  return (
    <div key={stats.statsMode ? 'stats' : 'normal'} className={`lu-hint sm-hint ${stats.statsMode ? 'stats' : ''} ${className}`}>
      {stats.statsMode
        ? (fr ? 'Mode stats : touche un joueur pour voir son profil. Garde une carte appuyée 1 seconde pour revenir.' : 'Stats mode: tap a player to see his profile. Hold a card for 1 second to go back.')
        : (fr ? 'Garde le doigt 1 seconde sur un joueur pour ouvrir son profil.' : 'Hold a player for 1 second to open his profile.')}
    </div>
  );
}
